<template>
  <section class="kids-catalog" aria-labelledby="kids-catalog-heading">
    <header class="kids-catalog__header">
      <h2 id="kids-catalog-heading" class="kids-catalog__title">Shop by stage</h2>
      <p class="kids-catalog__subtitle">
        Gear for every development stage — filter by sport and category.
      </p>
    </header>

    <div v-if="loading" class="kids-catalog__status" aria-live="polite">
      <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
      <span>Loading products…</span>
    </div>

    <p v-else-if="loadError" class="kids-catalog__status kids-catalog__status--error" role="alert">
      {{ loadError }}
      <button type="button" class="kids-catalog__retry" @click="fetchProducts">Try again</button>
    </p>

    <template v-else>
    <!-- Search -->
    <div class="kids-catalog__search">
      <div class="kids-catalog__search-wrap">
        <i class="fa-solid fa-magnifying-glass kids-catalog__search-icon" aria-hidden="true" />
        <input
          v-model="searchQuery"
          type="search"
          class="kids-catalog__search-input"
          placeholder="Search by name or brand…"
          autocomplete="off"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="kids-catalog__search-clear"
          aria-label="Clear search"
          @click="searchQuery = ''"
        >
          <i class="fa-solid fa-xmark" />
        </button>
      </div>
      <p v-if="searchQuery.trim()" class="kids-catalog__search-meta">
        {{ filteredProducts.length }} result{{ filteredProducts.length === 1 ? "" : "s" }}
      </p>
    </div>

    <!-- Development stage -->
    <div class="kids-catalog__filter-block">
      <span class="kids-catalog__filter-label">Development stage</span>
      <div class="kids-catalog__pills kids-catalog__pills--scroll kids-catalog__pills--stage" role="tablist" aria-label="Development stage">
        <button
          v-for="opt in stageOptions"
          :key="opt.value"
          type="button"
          role="tab"
          class="kids-catalog__pill"
          :class="{ 'kids-catalog__pill--active': activeStage === opt.value }"
          :aria-selected="activeStage === opt.value"
          @click="setStage(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <!-- Sport (advanced only) -->
    <div v-if="showSportFilter" class="kids-catalog__filter-block">
      <span class="kids-catalog__filter-label">Sport</span>
      <div class="kids-catalog__pills kids-catalog__pills--scroll kids-catalog__pills--sport" role="tablist" aria-label="Sport">
        <button
          v-for="opt in sportOptions"
          :key="opt.value"
          type="button"
          class="kids-catalog__pill kids-catalog__pill--sport"
          :class="{ 'kids-catalog__pill--active': activeSport === opt.value }"
          @click="setSport(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <!-- Subcategory -->
    <div v-if="visibleSubcategories.length > 1" class="kids-catalog__filter-block">
      <span class="kids-catalog__filter-label">Category</span>
      <div class="kids-catalog__pills kids-catalog__pills--scroll kids-catalog__pills--category">
        <button
          v-for="sub in visibleSubcategories"
          :key="sub.slug"
          type="button"
          class="kids-catalog__pill kids-catalog__pill--sub"
          :class="{ 'kids-catalog__pill--active': activeSubcategory === sub.slug }"
          @click="setSubcategory(sub.slug)"
        >
          {{ sub.label }}
        </button>
      </div>
    </div>

    <!-- Sort -->
    <div class="kids-catalog__toolbar">
      <label class="kids-catalog__sort">
        <span class="kids-catalog__sort-label">Sort</span>
        <select v-model="sortBy" class="kids-catalog__sort-select">
          <option value="featured">Featured</option>
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
          <option value="name">Name A–Z</option>
        </select>
      </label>
      <span class="kids-catalog__count">{{ filteredProducts.length }} items</span>
    </div>

    <!-- Grid -->
    <div v-if="filteredProducts.length" class="kids-catalog__grid">
      <article
        v-for="(product, index) in filteredProducts"
        :key="product.item_id"
        class="kids-product-card"
        :style="{ '--card-delay': `${index * 0.06}s` }"
      >
        <div class="kids-product-card__media">
          <span class="kids-product-card__stage">{{ stageBadge(product) }}</span>
          <span v-if="product.sport_code" class="kids-product-card__sport">
            {{ sportLabel(product.sport_code) }}
          </span>
          <span v-if="product.discounted" class="kids-product-card__sale">Sale</span>
          <span v-if="!product.is_in_stock" class="kids-product-card__oos">Out of stock</span>
          <img
            :src="product.thumbnail"
            :alt="product.name"
            class="kids-product-card__img"
            loading="lazy"
            decoding="async"
            @error="onImageError(product.item_id)"
          />
        </div>

        <div class="kids-product-card__body">
          <p class="kids-product-card__category">{{ subcategoryLabel(product.sub_category) }}</p>
          <h3 class="kids-product-card__name">{{ product.name }}</h3>
          <p v-if="product.brand" class="kids-product-card__brand">{{ product.brand }}</p>
          <p class="kids-product-card__desc">{{ product.short_description }}</p>
          <p v-if="product.size" class="kids-product-card__size">
            <i class="fa-solid fa-ruler-horizontal" aria-hidden="true" />
            {{ product.size }}
          </p>

          <div class="kids-product-card__price-row">
            <template v-if="product.discounted">
              <span class="kids-product-card__price kids-product-card__price--was">
                KES {{ formatPrice(product.price) }}
              </span>
              <span class="kids-product-card__price kids-product-card__price--now">
                KES {{ formatPrice(product.final_price) }}
              </span>
            </template>
            <span v-else class="kids-product-card__price">
              KES {{ formatPrice(product.final_price ?? product.price) }}
            </span>
          </div>

          <button type="button" class="kids-product-card__cta" @click="viewDetails(product)">
            View details
            <i class="fa-solid fa-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </article>
    </div>

    <div v-else class="kids-catalog__empty">
      <i class="fa-solid fa-box-open kids-catalog__empty-icon" aria-hidden="true" />
      <p v-if="hasActiveFilters">No products match your filters.</p>
      <p v-else>No kids products are listed yet. Check back soon.</p>
      <button v-if="hasActiveFilters" type="button" class="kids-catalog__reset" @click="resetFilters">
        Reset filters
      </button>
    </div>
    </template>
  </section>
</template>

<script>
import { buildApiUrl } from "@/config/api";
import {
  KIDS_STAGE_ADVANCED,
  KIDS_SPORTS,
  KIDS_STAGES,
  isAdvancedStage,
  sportLabel,
  stageLabel,
  subcategoriesForStage,
} from "@/constants/kidsCatalogue";
import { KIDS_API, KIDS_DETAIL_ROUTE } from "../constants";

const ALL_STAGE = "all";
const ALL_SPORT = "all";
const ALL_SUB = "all";

export default {
  name: "KidsProductCatalog",
  data() {
    return {
      products: [],
      loading: true,
      loadError: "",
      searchQuery: "",
      activeStage: ALL_STAGE,
      activeSport: ALL_SPORT,
      activeSubcategory: ALL_SUB,
      sortBy: "featured",
      imageErrors: {},
      fetchDebounce: null,
    };
  },
  computed: {
    stageOptions() {
      return [
        { value: ALL_STAGE, label: "All stages" },
        ...KIDS_STAGES.map((s) => ({ value: s.stage, label: s.label })),
      ];
    },
    sportOptions() {
      return [
        { value: ALL_SPORT, label: "All sports" },
        ...KIDS_SPORTS.map((s) => ({ value: s.sport, label: s.label })),
      ];
    },
    showSportFilter() {
      return this.activeStage === KIDS_STAGE_ADVANCED || this.activeStage === ALL_STAGE;
    },
    visibleSubcategories() {
      const all = { slug: ALL_SUB, label: "All categories" };
      if (this.activeStage === ALL_STAGE) {
        const slugs = new Map();
        this.products.forEach((p) => {
          const label = this.subcategoryLabel(p.sub_category);
          if (!slugs.has(p.sub_category)) slugs.set(p.sub_category, label);
        });
        return [
          all,
          ...Array.from(slugs, ([slug, label]) => ({ slug, label })),
        ];
      }
      const sport =
        isAdvancedStage(this.activeStage) && this.activeSport !== ALL_SPORT
          ? this.activeSport
          : null;
      return [all, ...subcategoriesForStage(this.activeStage, sport)];
    },
    filteredProducts() {
      return this.sortProducts([...this.products]);
    },
    hasActiveFilters() {
      return Boolean(
        this.searchQuery?.trim()
        || this.activeStage !== ALL_STAGE
        || this.activeSport !== ALL_SPORT
        || this.activeSubcategory !== ALL_SUB,
      );
    },
  },
  watch: {
    activeStage() {
      this.scheduleFetch();
    },
    activeSport() {
      this.scheduleFetch();
    },
    activeSubcategory() {
      this.scheduleFetch();
    },
    searchQuery() {
      this.scheduleFetch();
    },
  },
  mounted() {
    this.fetchProducts();
  },
  beforeUnmount() {
    clearTimeout(this.fetchDebounce);
  },
  methods: {
    sportLabel,
    stageLabel,
    subcategoryLabel(slug) {
      const subs = subcategoriesForStage(1).concat(
        subcategoriesForStage(2),
        subcategoriesForStage(3),
      );
      const hit = subs.find((s) => s.slug === slug);
      return hit?.label || slug.replace(/-/g, " ");
    },
    stageBadge(product) {
      const short = {
        1: "Early",
        2: "Structured",
        3: "Advanced",
      };
      return short[product.development_stage] || stageLabel(product.development_stage);
    },
    formatPrice(value) {
      return Number(value || 0).toLocaleString("en-KE");
    },
    setStage(value) {
      this.activeStage = value;
      this.activeSport = ALL_SPORT;
      this.activeSubcategory = ALL_SUB;
    },
    setSport(value) {
      this.activeSport = value;
      this.activeSubcategory = ALL_SUB;
    },
    setSubcategory(slug) {
      this.activeSubcategory = slug;
    },
    resetFilters() {
      this.searchQuery = "";
      this.activeStage = ALL_STAGE;
      this.activeSport = ALL_SPORT;
      this.activeSubcategory = ALL_SUB;
      this.sortBy = "featured";
      this.fetchProducts();
    },
    scheduleFetch() {
      clearTimeout(this.fetchDebounce);
      this.fetchDebounce = setTimeout(() => this.fetchProducts(), 300);
    },
    async fetchProducts() {
      this.loading = true;
      this.loadError = "";
      try {
        const params = new URLSearchParams();
        if (this.activeStage !== ALL_STAGE) {
          params.set("development_stage", String(this.activeStage));
        }
        if (this.activeSport !== ALL_SPORT) {
          params.set("sport_code", String(this.activeSport));
        }
        if (this.activeSubcategory !== ALL_SUB) {
          params.set("sub_category", this.activeSubcategory);
        }
        if (this.searchQuery?.trim()) {
          params.set("search", this.searchQuery.trim());
        }

        const qs = params.toString();
        const url = buildApiUrl(KIDS_API.list + (qs ? `?${qs}` : ""));
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || "Could not load kids products.");
        }
        this.products = Array.isArray(data.items) ? data.items : [];
      } catch (err) {
        this.loadError = err.message || "Could not load products.";
        this.products = [];
      } finally {
        this.loading = false;
      }
    },
    viewDetails(product) {
      this.$router.push({
        name: KIDS_DETAIL_ROUTE,
        params: { slug: product.slug },
      });
    },
    sortProducts(list) {
      const copy = [...list];
      switch (this.sortBy) {
        case "price-asc":
          return copy.sort((a, b) => a.price - b.price);
        case "price-desc":
          return copy.sort((a, b) => b.price - a.price);
        case "name":
          return copy.sort((a, b) => a.name.localeCompare(b.name));
        default:
          return copy;
      }
    },
    onImageError(itemId) {
      this.imageErrors = { ...this.imageErrors, [itemId]: true };
    },
  },
};
</script>

<style scoped>
.kids-catalog {
  --kids-pad-x: clamp(0.875rem, 3.5vw, 2.75rem);
  width: 100%;
  max-width: 100%;
  margin: clamp(1.25rem, 3vw, 2.5rem) 0 0;
  padding-inline: var(--kids-pad-x);
  padding-bottom: clamp(1rem, 3vw, 2rem);
  box-sizing: border-box;
}

.kids-catalog__header {
  text-align: center;
  margin-bottom: clamp(1rem, 2.5vw, 1.75rem);
  width: 100%;
}

.kids-catalog__title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.15rem, 3.2vw, 1.85rem);
  font-weight: 800;
  color: var(--text-color1);
  text-wrap: balance;
}

.kids-catalog__subtitle {
  margin: 0 auto;
  font-size: clamp(0.78rem, 2vw, 0.95rem);
  color: var(--text-color2);
  max-width: min(56ch, 100%);
  line-height: 1.5;
  text-wrap: pretty;
}

.kids-catalog__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: clamp(2rem, 5vw, 3rem) 1rem;
  color: var(--text-color2);
  font-size: 0.92rem;
}

.kids-catalog__status--error {
  flex-direction: column;
  color: #c62828;
  text-align: center;
}

.kids-catalog__retry {
  margin-top: 0.35rem;
  padding: 0.45rem 1rem;
  border: none;
  border-radius: 999px;
  background: var(--border-color-hover);
  color: #fff;
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
}

.kids-catalog__search {
  margin-bottom: clamp(1rem, 2.5vw, 1.35rem);
  width: 100%;
}

.kids-catalog__search-wrap {
  position: relative;
  width: 100%;
  max-width: min(100%, 720px);
  margin: 0 auto;
}

.kids-catalog__search-icon {
  position: absolute;
  left: clamp(0.75rem, 2vw, 1rem);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  pointer-events: none;
  font-size: clamp(0.85rem, 2vw, 0.95rem);
}

.kids-catalog__search-input {
  width: 100%;
  padding: clamp(0.65rem, 2vw, 0.8rem) 2.5rem clamp(0.65rem, 2vw, 0.8rem) 2.65rem;
  border-radius: 999px;
  border: 1px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color1);
  font: inherit;
  font-size: clamp(0.85rem, 2.2vw, 0.95rem);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.kids-catalog__search-input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.kids-catalog__search-clear {
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--text-color2);
  cursor: pointer;
  padding: 0.35rem;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
}

.kids-catalog__search-meta {
  text-align: center;
  margin: 0.5rem 0 0;
  font-size: clamp(0.75rem, 1.8vw, 0.82rem);
  color: var(--text-color2);
}

.kids-catalog__filter-block {
  margin-bottom: clamp(0.75rem, 2vw, 1rem);
  width: 100%;
}

.kids-catalog__filter-label {
  display: block;
  font-size: clamp(0.68rem, 1.6vw, 0.72rem);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-color2);
  margin-bottom: 0.45rem;
  padding-inline: 0.1rem;
}

.kids-catalog__pills {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(0.35rem, 1.2vw, 0.5rem);
  width: 100%;
}

.kids-catalog__pills--scroll {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: flex-start;
  padding-bottom: 0.4rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scroll-snap-type: x proximity;
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 0.5rem,
    #000 calc(100% - 0.5rem),
    transparent
  );
}

.kids-catalog__pills--stage,
.kids-catalog__pills--sport,
.kids-catalog__pills--category {
  padding-inline: 0.1rem;
}

@media (min-width: 900px) {
  .kids-catalog__pills--stage {
    flex-wrap: wrap;
    overflow-x: visible;
    mask-image: none;
    justify-content: center;
  }

  .kids-catalog__pills--sport,
  .kids-catalog__pills--category {
    flex-wrap: wrap;
    overflow-x: visible;
    mask-image: none;
  }
}

.kids-catalog__pill {
  border: 1px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color1);
  font: inherit;
  font-size: clamp(0.7rem, 1.8vw, 0.8rem);
  font-weight: 600;
  padding: clamp(0.4rem, 1.2vw, 0.5rem) clamp(0.65rem, 2vw, 0.9rem);
  min-height: 40px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  scroll-snap-align: start;
  transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s;
}

.kids-catalog__pill:hover {
  border-color: var(--border-color-hover);
}

.kids-catalog__pill--active {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  color: var(--text-color3);
}

.kids-catalog__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: clamp(1rem, 2.5vw, 1.35rem) 0 clamp(0.85rem, 2vw, 1rem);
  padding: clamp(0.55rem, 1.5vw, 0.7rem) 0;
  border-top: 1px solid var(--background-color3);
  border-bottom: 1px solid var(--background-color3);
  flex-wrap: wrap;
  width: 100%;
}

.kids-catalog__sort {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(0.8rem, 2vw, 0.88rem);
  flex: 1 1 auto;
  min-width: min(100%, 200px);
}

.kids-catalog__sort-select {
  font: inherit;
  flex: 1;
  max-width: 220px;
  padding: 0.45rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color1);
  min-height: 40px;
}

.kids-catalog__count {
  font-size: clamp(0.78rem, 1.8vw, 0.85rem);
  color: var(--text-color2);
  font-weight: 600;
  white-space: nowrap;
}

.kids-catalog__grid {
  display: grid;
  width: 100%;
  gap: clamp(0.75rem, 2vw, 1.35rem);
  grid-template-columns: 1fr;
}

@media (min-width: 400px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 768px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1100px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (min-width: 1440px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (min-width: 1920px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

.kids-product-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: clamp(10px, 1.5vw, 14px);
  overflow: hidden;
  animation: kidsCardIn 0.45s ease backwards;
  animation-delay: var(--card-delay, 0s);
  transition: transform 0.22s, box-shadow 0.22s;
}

.kids-product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 28px color-mix(in srgb, var(--text-color1) 8%, transparent);
}

.kids-product-card__media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: var(--background-color2);
  overflow: hidden;
}

.kids-product-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.kids-product-card__stage,
.kids-product-card__sport {
  position: absolute;
  top: clamp(0.4rem, 1.2vw, 0.55rem);
  font-size: clamp(0.58rem, 1.4vw, 0.65rem);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  z-index: 1;
}

.kids-product-card__stage {
  left: clamp(0.4rem, 1.2vw, 0.55rem);
  background: color-mix(in srgb, var(--border-color-hover) 88%, #000);
  color: var(--text-color3);
}

.kids-product-card__sport {
  left: auto;
  right: clamp(0.4rem, 1.2vw, 0.55rem);
  background: rgba(255, 255, 255, 0.92);
  color: var(--text-color1);
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kids-product-card__sale {
  position: absolute;
  bottom: clamp(0.4rem, 1.2vw, 0.55rem);
  left: clamp(0.4rem, 1.2vw, 0.55rem);
  background: #e53935;
  color: #fff;
  font-size: clamp(0.58rem, 1.4vw, 0.65rem);
  font-weight: 800;
  padding: 0.22rem 0.45rem;
  border-radius: 6px;
  z-index: 1;
}

.kids-product-card__oos {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: clamp(0.72rem, 1.8vw, 0.8rem);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  z-index: 2;
  text-align: center;
  padding: 0.5rem;
}

.kids-product-card__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: clamp(0.7rem, 2vw, 0.95rem);
  text-align: left;
  min-width: 0;
}

.kids-product-card__category {
  margin: 0 0 0.2rem;
  font-size: clamp(0.62rem, 1.5vw, 0.68rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--border-color-hover);
}

.kids-product-card__name {
  margin: 0 0 0.2rem;
  font-size: clamp(0.88rem, 2.2vw, 0.98rem);
  font-weight: 700;
  line-height: 1.3;
  color: var(--text-color1);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.kids-product-card__brand {
  margin: 0 0 0.3rem;
  font-size: clamp(0.72rem, 1.8vw, 0.78rem);
  color: var(--text-color2);
}

.kids-product-card__desc {
  margin: 0 0 0.45rem;
  font-size: clamp(0.72rem, 1.8vw, 0.8rem);
  line-height: 1.45;
  color: var(--text-color2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.kids-product-card__size {
  margin: 0 0 0.45rem;
  font-size: clamp(0.68rem, 1.6vw, 0.75rem);
  color: var(--text-color2);
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.kids-product-card__price-row {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-bottom: clamp(0.55rem, 1.5vw, 0.75rem);
}

.kids-product-card__price {
  font-size: clamp(0.9rem, 2.2vw, 1rem);
  font-weight: 800;
  color: var(--text-color1);
}

.kids-product-card__price--was {
  font-size: clamp(0.75rem, 1.8vw, 0.82rem);
  font-weight: 500;
  text-decoration: line-through;
  color: var(--text-color2);
}

.kids-product-card__price--now {
  color: #2e7d32;
}

.kids-product-card__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  padding: clamp(0.5rem, 1.5vw, 0.6rem) 1rem;
  min-height: 42px;
  border: 1px solid var(--border-color-hover);
  border-radius: 10px;
  background: color-mix(in srgb, var(--border-color-hover) 10%, transparent);
  color: var(--border-color-hover);
  font: inherit;
  font-size: clamp(0.78rem, 1.8vw, 0.85rem);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, transform 0.2s;
}

.kids-product-card__cta:hover {
  background: var(--border-color-hover);
  color: #fff;
  transform: translateY(-1px);
}

.kids-catalog__empty {
  text-align: center;
  padding: clamp(2rem, 6vw, 4rem) clamp(0.75rem, 3vw, 1.5rem);
  color: var(--text-color2);
  width: 100%;
}

.kids-catalog__empty-icon {
  font-size: clamp(2rem, 6vw, 2.5rem);
  opacity: 0.45;
  margin-bottom: 0.75rem;
}

.kids-catalog__reset {
  margin-top: 0.75rem;
  padding: 0.55rem 1.1rem;
  min-height: 44px;
  border: none;
  border-radius: 999px;
  background: var(--border-color-hover);
  color: #fff;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

@keyframes kidsCardIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1024px) {
  .kids-catalog {
    --kids-pad-x: clamp(0.75rem, 3vw, 1.75rem);
    margin-top: clamp(0.85rem, 2.5vw, 1.5rem);
  }

  .kids-catalog__header {
    margin-bottom: clamp(0.75rem, 2vw, 1.25rem);
  }
}

@media (max-width: 768px) {
  .kids-catalog {
    --kids-pad-x: clamp(0.65rem, 4vw, 1.25rem);
    padding-bottom: clamp(0.85rem, 4vw, 1.5rem);
  }

  .kids-catalog__title {
    font-size: clamp(1rem, 4.2vw, 1.35rem);
  }

  .kids-catalog__subtitle {
    font-size: clamp(0.72rem, 2.6vw, 0.85rem);
  }

  .kids-catalog__search-input {
    font-size: max(16px, 0.9rem);
    padding-right: 2.75rem;
  }

  .kids-catalog__filter-label {
    font-size: 0.65rem;
    margin-bottom: 0.35rem;
  }

  .kids-catalog__pill {
    font-size: clamp(0.65rem, 2.8vw, 0.75rem);
    padding: 0.38rem 0.7rem;
    min-height: 38px;
  }

  .kids-product-card__body {
    padding: clamp(0.55rem, 2.5vw, 0.75rem);
  }

  .kids-product-card__name {
    font-size: clamp(0.82rem, 3.2vw, 0.92rem);
  }

  .kids-product-card__desc {
    -webkit-line-clamp: 3;
    font-size: 0.72rem;
  }

  .kids-product-card__cta {
    min-height: 44px;
    font-size: 0.76rem;
  }
}

@media (max-width: 640px) {
  .kids-catalog__toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .kids-catalog__sort {
    width: 100%;
  }

  .kids-catalog__sort-select {
    max-width: none;
    flex: 1;
  }

  .kids-catalog__count {
    text-align: center;
  }
}

@media (max-width: 399px) {
  .kids-catalog__grid {
    gap: 0.65rem;
    grid-template-columns: 1fr;
  }

  .kids-product-card__sport {
    display: none;
  }

  .kids-product-card__stage {
    font-size: 0.55rem;
    padding: 0.2rem 0.4rem;
  }

  .kids-catalog__pills--scroll {
    gap: 0.3rem;
    padding-bottom: 0.35rem;
  }
}

@media (max-width: 360px) {
  .kids-catalog {
    --kids-pad-x: 0.6rem;
    margin-top: 0.75rem;
  }

  .kids-catalog__pill {
    font-size: 0.62rem;
    padding: 0.35rem 0.55rem;
  }

  .kids-product-card__price {
    font-size: 0.85rem;
  }

  .kids-product-card__category {
    font-size: 0.58rem;
  }
}

@media (min-width: 400px) and (max-width: 599px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: clamp(0.55rem, 2.5vw, 0.85rem);
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .kids-catalog__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .kids-catalog {
    margin-top: 0.65rem;
  }

  .kids-catalog__header {
    margin-bottom: 0.5rem;
  }

  .kids-catalog__filter-block {
    margin-bottom: 0.5rem;
  }

  .kids-catalog__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.65rem;
  }
}

@media (min-width: 1536px) {
  .kids-catalog {
    --kids-pad-x: clamp(2rem, 4vw, 4rem);
  }
}

@media (hover: none) and (pointer: coarse) {
  .kids-catalog__pill {
    min-height: 44px;
  }

  .kids-catalog__search-input {
    font-size: max(16px, 0.9rem);
  }

  .kids-product-card:hover {
    transform: none;
  }

  .kids-product-card__cta {
    min-height: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kids-product-card {
    animation: none;
  }
}
</style>
